import {search} from '@apps-common/js-client/api/items';
import {useQuery} from '@tanstack/react-query';
import keyBy from 'lodash/keyBy';
import {DURATION_MS} from './util.js';
import {request} from '@api/index.js';
import urls from '@api/urls.js';
import {GOOD_ITEM_TYPES} from '@src/constants/item-types.ts';

const getItemTypes = () =>
  request
    .get(`${urls.data}/item-types`)
    .then(response => response.data.item_types);

export const getItem = ({itemType, itemId}) =>
  request
    .get(`${urls.data}/item-types/${itemType}/items/${itemId}`)
    .then(response => response.data);

const getItemsById = ({itemTypes, itemIds}) => {
  return search({
    types: itemTypes,
    filter: {
      type: 'StringInFilter',
      field_name: 'id',
      config: itemIds,
    },
  });
};

export const useGetItemTypes = (queryOptions = {}) =>
  useQuery({
    queryKey: ['itemTypes'],
    queryFn: getItemTypes,
    // this data does not change frequently
    staleTime: DURATION_MS.TWELVE_HOURS,
    ...queryOptions,
  });

export const useGetItemTypesById = () =>
  useGetItemTypes({
    select: itemTypes => keyBy(itemTypes, 'id'),
  });

export const useGetItemTypesLabels = (itemTypeIds = []) =>
  useGetItemTypes({
    select: itemTypes => {
      const byId = keyBy(itemTypes, 'id');
      return itemTypeIds.map(id => byId[id]?.display_name ?? id);
    },
  });

export const useGetItemTypeLabel = itemTypeId =>
  useGetItemTypes({
    placeholder: itemTypeId,
    select: itemTypes => {
      const byId = keyBy(itemTypes, 'id');
      return byId[itemTypeId]?.display_name ?? itemTypeId;
    },
  });

// We have a list of item types that are supported when searching
export const useGetAvailableItemTypes = () =>
  useGetItemTypes({
    select: itemTypes => {
      const filtered = itemTypes.filter(
        itemType => itemType.id in GOOD_ITEM_TYPES,
      );

      return keyBy(filtered, 'id');
    },
  });

export const useGetItemsById = ({itemTypes, itemIds}, queryOptions = {}) =>
  useQuery({
    queryKey: ['itemsById', {itemTypes, itemIds}],
    queryFn: () => getItemsById({itemTypes, itemIds}),
    ...queryOptions,
  });
